<template>
	<div class="order-status-page">
		<div
			class="desktop-center"
			v-if="this.orderStatus.sub_orders != undefined"
		>
			<div
				class="order-banner"
				v-sk-banner-background="backgroundFileSuffix"
			>
				<div class="order-banner-bottom">
					<span class="order-banner-title">
						{{ orderBannerTitle }}
					</span>
					<span class="order-banner-subtitle">#{{ orderId }}</span>
				</div>
			</div>
			<div class="order-status">
				<div class="order-eta" v-if="showEta">
					<span class="order-eta-label">
						<i class="sk-icon-clock-solid"></i>
						{{ etaLabel }}
					</span>
					<span>
						{{ etaText }}
					</span>
				</div>
				<div
					v-if="orderCancelled"
					class="sk-widget order-status-widget"
				>
					<div>
						<div class="order-status-title">
							Order #{{ orderId }} Successfully Cancelled
						</div>
						<div class="order-status-description">
							A refund will be applied to the card associated with
							your order shortly.
						</div>
					</div>
				</div>
				<div class="order-progress" v-if="showOrderProgress">
					<svg
						class="progress-timeline"
						:style="progressTimelineStyle"
						:viewBox="viewBoxDimensions"
					>
						<line
							x1="12.5"
							:y1="firstIndicatorY"
							x2="12.5"
							:y2="firstIndicatorY2"
							stroke-width="1"
							stroke="#EEEEEE"
						/>
						<line
							x1="12.5"
							:y1="firstIndicatorY"
							x2="12.5"
							:y2="lastIndicatorY"
							stroke-width="1"
							stroke="#FF7000"
						/>
					</svg>
					<div>
						<div class="progress-milestone">
							<div
								class="progress-indicator complete"
								:class="{
									'last-complete':
										lastMilestoneCompleted == 'created'
								}"
							></div>
							<div class="progress-description">
								<div>Order Placed</div>
								<div>{{ createdTime }}</div>
							</div>
						</div>
						<div class="progress-milestone">
							<div
								class="progress-indicator"
								:class="{
									'last-complete':
										lastMilestoneCompleted == 'accepted',
									complete: acceptedMilestoneComplete
								}"
							></div>
							<div class="progress-description">
								<div>Preparing Order</div>
								<div>{{ acceptedTime }}</div>
							</div>
						</div>
						<div class="progress-milestone">
							<div
								class="progress-indicator"
								:class="{
									'last-complete':
										lastMilestoneCompleted == 'pickedup',
									complete: pickedUpMilestoneComplete
								}"
							></div>
							<div class="progress-description">
								<div>{{ pickedUpText }}</div>
								<div>{{ pickedUpTime }}</div>
							</div>
						</div>
						<div
							class="progress-milestone"
							v-if="showDeliveredMilestone"
						>
							<div
								class="progress-indicator"
								:class="{
									'last-complete':
										lastMilestoneCompleted == 'delivered',
									complete: deliveredMilestoneComplete
								}"
							></div>
							<div class="progress-description">
								<div>Delivered</div>
								<div>{{ deliveredTime }}</div>
							</div>
						</div>
					</div>
				</div>
				<div class="sk-widget order-status-widget">
					<div>
						<div class="order-status-title">
							Help Your Courier Find You
						</div>
						<div class="order-status-description">
							{{ deliveryHintText }}
						</div>
					</div>
					<div v-if="showLocation">
						<span class="location-icon">
							<div>{{ locationTitle }}</div>
							<div>{{ orderStatus.table }}</div>
						</span>
					</div>
				</div>
				<div class="sk-widget order-status-widget" v-if="showCourier">
					<div>
						<div class="order-status-title">Delivery</div>
						<div class="order-status-description">
							{{ courierName }}
						</div>
					</div>
					<img
						class="courier-image"
						:src="courierImagePath"
						alt="Courier Service Image"
					/>
				</div>
				<div class="sk-widget order-status-widget">
					<div>
						<div class="order-status-title">Prepared By</div>
						<div class="order-status-description">
							{{ restaurantName }}
						</div>
					</div>
				</div>
				<div class="sk-widget order-status-widget">
					<div>
						<div class="order-status-title">
							Questions? Comments?
						</div>
						<div
							class="order-status-description"
							v-html="supportHintText"
						></div>
					</div>
					<a
						class="
							button button-auto button-primary
							order-status-button
						"
						href="sms:+18559823181"
					>
						<i class="sk-icon-comment-lines-solid"></i>
					</a>
				</div>
				<div
					class="sk-widget order-status-widget"
					v-if="showCancelOrder"
				>
					<div>
						<div class="order-status-title">Change Your Mind?</div>
						<div class="order-status-description">
							You can cancel your order up to 60 minutes before
							your selected delivery time
						</div>
					</div>
					<button
						class="
							button button-auto button-secondary
							order-status-button
						"
						@click="cancelOrder"
					>
						<i class="sk-icon-times"></i>
					</button>
				</div>
			</div>
		</div>
		<ConfirmationModal ref="confirmationModal" />
	</div>
</template>

<style scoped>
.order-status-page {
	height: calc(100% - 90px);
	overflow-y: auto;
	overflow-x: hidden;
	padding-bottom: 20px;
}

.desktop-center {
	max-width: 700px !important;
}

.order-banner {
	height: 24%;
	justify-content: flex-end !important;
}

.order-banner-bottom {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin: 0 10px;
	padding: 0 10px;
	width: calc(100% - 20px);
	background: var(--gR-base-80);
	height: 45px;
	border-top-right-radius: 5px;
	border-top-left-radius: 5px;
}

.order-banner-title,
.order-banner-subtitle {
	color: var(--gR-white);
	font-weight: 600;
}

.order-banner-title {
	font-size: 18px;
}

.order-banner-subtitle {
	font-size: 12px;
}

.order-status {
	margin: 0 10px;
}

.order-status .order-status-widget:first-child {
	margin-top: 20px;
}

.order-eta {
	display: flex;
	justify-content: space-between;
	align-items: center;
	height: 50px;
	width: 100%;
	background: var(--gR-white);
	border-bottom-right-radius: 5px;
	border-bottom-left-radius: 5px;
	padding: 0 10px;
	box-shadow: 0px 0px 2px rgba(136, 136, 136, 0.25);
	font-size: 12px;
}

.order-eta-label {
	display: flex;
	align-items: center;
}

.order-eta-label i {
	margin-right: 15px;
}

.order-eta .order-eta-label {
	color: var(--gR-base-80);
	font-weight: 600;
}

.order-eta span {
	color: var(--sk-grey3);
}

.order-progress {
	display: flex;
	align-items: flex-start;
	margin: 35px 0 35px 15px;
}

.progress-timeline {
	width: 25px;
	height: 280px;
}

.progress-milestone {
	display: flex;
	align-items: center;
	position: relative;
	height: 39px;
}

.progress-milestone + .progress-milestone {
	margin-top: 45px;
}

.progress-indicator {
	border-radius: 50%;
	background: var(--gR-white);
	border: 1px solid var(--gR-white2);
	width: 25px;
	height: 25px;
	position: absolute;
	left: -25px;
	top: calc(50% - 10px);
	display: flex;
	justify-content: center;
	align-items: center;
}

.progress-indicator.complete {
	border: 1px solid var(--gR-white2);
}

.progress-indicator.last-complete {
	border: 1px solid var(--gR-teal-70);
}

.progress-indicator.complete::before,
.progress-indicator.last-complete::before {
	background: var(--gR-teal-70);
	width: 15px;
	height: 15px;
	content: "";
	border-radius: 50%;
}

.progress-description {
	margin-left: 20px;
}

.progress-description div:last-child {
	font-size: 12px;
	color: var(--sk-grey3);
}

.progress-description div:first-child {
	font-size: 14px;
	font-weight: 700;
	color: var(--gR-base-80);
}

.order-status-widget {
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.order-status-widget:last-child {
	margin-bottom: 0;
}

.order-status-title {
	font-size: 14px;
	font-weight: 600;
	color: var(--gR-base-80);
	margin-bottom: 10px;
}

.order-status-description {
	font-size: 12px;
	color: var(--sk-grey3);
}

.courier-image {
	height: 40px;
	margin-right: 15px;
}

.order-status-button {
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-right: 15px;
}

.order-status-button i {
	font-size: 15px;
}

.location-icon {
	background: rgba(255, 133, 37, 0.5);
	border-radius: 5px;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	text-align: center;
	font-size: 10px;
	height: 40px;
	min-width: 40px;
}

.location-icon div {
	color: var(--gR-teal-70);
}

.location-icon div:last-child {
	font-weight: 600;
	font-size: 12px;
}

@media (min-width: 700px) {
	.order-banner {
		margin-top: 20px;
		border-radius: 5px !important;
	}
}
</style>

<script>
import moment from "moment"
import "moment-timezone"
import { MenuType } from "@reef-os/utils/enums/menuType"
import { DeliveryMode } from "@reef-os/utils/enums/deliveryMode"
import { ConfirmationModalType } from "@/enums/ConfirmationModalType"
import { CourierId } from "@reef-os/utils/enums/courierId"
import { OrderStatus } from "@reef-os/utils/enums/orderStatus"
import loaderMixin from "@/mixins/loader-mixin"
import orderSuccessMixin from "@/mixins/order-success-mixin"
import locationNameFormatterMixin from "@/mixins/location-name-formatter-mixin"
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue"

export default {
	name: "OrderStatus",
	mixins: [orderSuccessMixin, loaderMixin, locationNameFormatterMixin],
	components: {
		ConfirmationModal
	},
	data() {
		return {
			timer: "",
			firstIndicatorY: 10
		}
	},
	created() {
		this.timer = setInterval(this.getOrderStatus, 60000)
		this.$store.dispatch("setInPurchaseState", false)
	},
	computed: {
		business() {
			return this.$store.state.business
		},
		backgroundFileSuffix() {
			let imgNameFull = this.$store.state.business.img
			if (!imgNameFull) {
				return ""
			}
			return (
				process.env.VUE_APP_BASE_IMAGE_URL +
				this.$route.params.slug +
				"/" +
				this.$store.state.business.img
			)
		},
		orderBannerTitle() {
			return this.orderCancelled ? "Order Cancelled" : "Track Your Order"
		},
		orderCancelled() {
			return this.orderStatus.cancelled
		},
		deliveryStatus() {
			return this.$store.state.deliveryStatus
		},
		preorderDate() {
			if (!this.orderStatus) {
				return null
			}
			if (this.orderStatus.scheduled_date) {
				return this.orderStatus.scheduled_date
			} else if (this.firstSuborder.delivery_slot) {
				return this.firstSuborder.delivery_slot
			}
			return null
		},
		hasSuborders() {
			return (
				this.orderStatus.sub_orders != undefined &&
				this.orderStatus.sub_orders.length > 0
			)
		},
		firstSuborder() {
			return this.hasSuborders ? this.orderStatus.sub_orders[0] : {}
		},
		isCourierWalking() {
			return (
				this.hasSuborders &&
				this.firstSuborder.delivery_id == CourierId.WALKING
			)
		},
		isCourierTookan() {
			return (
				this.hasSuborders &&
				this.firstSuborder.delivery_id == CourierId.TOOKAN
			)
		},
		showEta() {
			const showingDeliverySlot =
				!this.acknowledgeTime && this.preorderDate

			return (
				this.etaText != "N/A" &&
				this.orderStatus.menu_type != MenuType.POPUP &&
				!this.orderCancelled &&
				(!this.isCourierWalking || showingDeliverySlot)
			)
		},
		etaLabel() {
			return this.preorderDate && !this.acknowledgeTime
				? "Scheduled Delivery"
				: "Estimated Time"
		},
		etaText() {
			if (!this.acknowledgeTime && this.preorderDate) {
				return moment
					.utc(this.preorderDate)
					.tz(this.business.timezone)
					.format("ddd. MMM D h:mm A")
			}
			if (
				this.deliveryStatus == null ||
				!this.deliveryStatus.dropoff_eta
			) {
				return "N/A"
			} else {
				const startEta = moment
					.utc(this.deliveryStatus.dropoff_eta)
					.tz(this.business.timezone)
					.add(-5, "m")
					.format("hh:mm A")
				const endEta = moment
					.utc(this.deliveryStatus.dropoff_eta)
					.tz(this.business.timezone)
					.add(5, "m")
					.format("hh:mm A")
				return `${startEta} - ${endEta}`
			}
		},
		createdTime() {
			if (!this.hasSuborders) {
				return null
			}
			return this.formatTime(moment.utc(this.orderStatus.created_time))
		},
		acknowledgeTime() {
			if (!this.hasSuborders || !this.firstSuborder.acknowledged_time) {
				return null
			}
			return this.formatTime(
				moment.utc(this.firstSuborder.acknowledged_time)
			)
		},
		acceptedTime() {
			if (!this.hasSuborders || !this.firstSuborder.accept_time) {
				return null
			}
			return this.formatTime(moment.utc(this.firstSuborder.accept_time))
		},
		pickedUpText() {
			if (this.orderStatus.menu_type == MenuType.POPUP) {
				return this.deliveryMode == DeliveryMode.PICKUP_AREA
					? "Ready for Pick Up"
					: "Ready"
			}
			return "Picked Up"
		},
		pickedUpTime() {
			if (!this.hasSuborders || !this.firstSuborder.pickup_time) {
				return null
			}
			return this.formatTime(moment.utc(this.firstSuborder.pickup_time))
		},
		deliveredTime() {
			if (!this.hasSuborders || !this.firstSuborder.delivered_time) {
				return null
			}
			return this.formatTime(
				moment.utc(this.firstSuborder.delivered_time)
			)
		},
		acceptedMilestoneComplete() {
			return this.hasSuborders && this.firstSuborder.accept_time != null
		},
		pickedUpMilestoneComplete() {
			if (!this.hasSuborders) {
				return false
			}
			const hasCompletedtime =
				this.firstSuborder.completed_time != null &&
				this.firstSuborder.completed_time != ""

			if (this.isCourierWalking || this.isCourierTookan) {
				return hasCompletedtime
			} else {
				return this.firstSuborder.pickup_time != null
			}
		},
		showDeliveredMilestone() {
			switch (this.deliveryMode) {
				case DeliveryMode.PICKUP_AREA:
					return this.orderStatus.menu_type != MenuType.POPUP
				default:
					return true
			}
		},
		deliveredMilestoneComplete() {
			if (!this.hasSuborders) {
				return false
			}
			return this.firstSuborder.delivered_time != null
		},
		lastMilestoneCompleted() {
			if (
				this.deliveredMilestoneComplete &&
				this.showDeliveredMilestone
			) {
				return "delivered"
			} else if (this.pickedUpMilestoneComplete) {
				return "pickedup"
			} else if (this.acceptedMilestoneComplete) {
				return "accepted"
			} else {
				return "created"
			}
		},
		showCourier() {
			return (
				!this.isCourierWalking &&
				!this.isCourierTookan &&
				!this.orderStatus.menu_type != MenuType.POPUP &&
				!this.orderCancelled
			)
		},
		showOrderProgress() {
			return !this.orderCancelled
		},
		showDeliveryInstructions() {
			return !this.orderCancelled
		},
		showCancelOrder() {
			return (
				this.preorderDate != null &&
				!this.acceptedMilestoneComplete &&
				!this.orderCancelled
			)
		},
		courierName() {
			if (!this.hasSuborders) {
				return ""
			}
			switch (this.firstSuborder.delivery_id) {
				case CourierId.CUT_CATS:
					return "Cut Cats"
				case CourierId.POSTMATES:
					return "Postmates"
				case CourierId.DOORDASH:
					return "DoorDash"
			}
			return ""
		},
		courierImagePath() {
			if (!this.hasSuborders) {
				return ""
			}
			switch (this.firstSuborder.delivery_id) {
				case CourierId.CUT_CATS:
					return "/images/cutcats.png"
				case CourierId.POSTMATES:
					return "/images/postmates.png"
				case CourierId.DOORDASH:
					return "/images/doordash.png"
			}
			return ""
		},
		restaurantName: function () {
			if (!this.hasSuborders) {
				return "N/A"
			}
			return this.firstSuborder.rest_name
		},
		showLocation() {
			return (
				(this.deliveryMode == DeliveryMode.DIRECT_TO_LOCATION ||
					this.deliveryMode == DeliveryMode.LAST_YARD) &&
				this.orderStatus.table != undefined &&
				this.orderStatus.table != null &&
				this.orderStatus.table.length > 0
			)
		},
		locationTitle() {
			return this.$options.filters.locationName(this.businessType)
		},
		lastIndicatorY() {
			const lastMilestone = this.lastMilestoneCompleted
			switch (lastMilestone) {
				case "created":
					return this.firstIndicatorY
				case "accepted":
					return 110
				case "pickedup":
					return 190
				case "delivered":
					return this.showDeliveredMilestone ? 275 : 190
			}
		},
		firstIndicatorY2() {
			return this.showDeliveredMilestone ? 280 : 190
		},
		viewBoxDimensions() {
			return `0 0 25 ${this.firstIndicatorY2}`
		},
		progressTimelineStyle() {
			return { height: this.firstIndicatorY2 }
		},
		supportHintText() {
			if (this.orderCancelled) {
				return "We're still available!<br> Text us for instant support!"
			}
			return "Text us for instant support!"
		}
	},
	methods: {
		getOrderStatus: function () {
			this.$store.dispatch("getOrderStatus", {
				orderId: this.orderId
			})
		},
		formatTime: function (date) {
			let format = "hh:mm A"
			if (this.preorderDate) {
				format = "ddd. MMM D h:mm A"
			}
			return date.tz(this.business.timezone).format(format)
		},
		async cancelOrder() {
			const confirmed = await this.$refs.confirmationModal.open({
				title: "Cancel your preorder?",
				body: "Are you sure you want to cancel your order? You will not be able to recover your order.",
				cancel: "Keep order",
				confirmation: "Cancel Order",
				type: ConfirmationModalType.RED,
				confirmationIcon: "sk-icon-times"
			})
			if (confirmed) {
				this.toggleLoader(true)
				this.$store
					.dispatch("updateOrderStatus", {
						orderId: this.orderId,
						id: this.$store.state.orderStatus.sub_orders[0].id,
						status: OrderStatus.CANCELLED
					})
					.then(() => {
						this.toggleLoader(false)
						window.scroll({ top: 0 })
					})
					.catch(() => {
						this.toggleLoader(false)
					})
			}
		}
	},
	beforeDestroy() {
		clearInterval(this.timer)
	}
}
</script>